import { useState } from 'react';

import { api } from '../../../lib/api-client';
import { useChecklists } from '../hooks/use-checklists';

/**
 * ChecklistAgentBanner — Fatia E.
 *
 * Banner discreto inline na conversa quando `checklist-agent.proposal-made`
 * WS event chega. Atendente aceita item-a-item (POST fulfill) ou descarta
 * tudo via X.
 *
 * Apenas propõe v1 — não auto-fulfilla (decisão §7 Tech Lead). Audit registra
 * proposal-accepted / proposal-rejected.
 */
export function ChecklistAgentBanner({
  conversationId,
}: {
  conversationId: string;
}): JSX.Element | null {
  const { latestProposal, clearProposal } = useChecklists(conversationId);
  const [acceptedIds, setAcceptedIds] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  if (!latestProposal || latestProposal.proposals.length === 0) return null;

  const onAccept = async (itemId: string): Promise<void> => {
    setBusy(true);
    try {
      await api.fulfillChecklistItem(itemId, {
        evidence: {
          source: 'checklist-agent',
          invocationId: latestProposal.invocationId,
        },
        notes: 'Aceito via proposta do checklist-agent',
      });
      setAcceptedIds((s) => {
        const next = new Set(s);
        next.add(itemId);
        return next;
      });
    } finally {
      setBusy(false);
    }
  };

  const remaining = latestProposal.proposals.filter((p) => !acceptedIds.has(p.itemId));
  if (remaining.length === 0) {
    // Todos aceitos; limpa banner.
    setTimeout(() => clearProposal(), 500);
    return null;
  }

  return (
    /* Fase C Brief 2A r7b — DS AI tokens (--ai-bg/--ai-fg/--ai-border) Spec §1.5. */
    <div
      className="border-l-4 px-3 py-2 m-2 rounded text-xs"
      style={{
        borderLeftColor: 'var(--ai-fg)',
        background: 'var(--ai-bg)',
      }}
      data-testid="checklist-agent-banner"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span
          className="font-medium uppercase tracking-wide text-2xs"
          style={{ color: 'var(--ai-fg)' }}
        >
          checklist-agent sugere
        </span>
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => clearProposal()}
          className="text-text-secondary hover:text-text-primary"
          aria-label="Descartar todas as sugestões"
          title="Descartar"
        >
          ×
        </button>
      </div>
      <ul className="space-y-1.5">
        {remaining.map((p) => (
          <li key={p.itemId} className="flex items-start gap-2">
            <div className="flex-1">
              <p className="font-medium text-text-primary">{p.itemName}</p>
              <p className="text-text-secondary italic text-2xs">{p.rationale}</p>
              <p className="text-2xs text-text-secondary mt-0.5">
                Confiança: {Math.round(p.confidence * 100)}%
              </p>
            </div>
            <button
              type="button"
              onClick={() => void onAccept(p.itemId)}
              disabled={busy}
              className="button button--primary text-2xs px-2 py-1 disabled:opacity-50"
              aria-label={`Aceitar proposta: marcar ${p.itemName} como cumprido`}
            >
              Marcar cumprido
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
